import { t } from "i18next";
import { useState } from "react";
import { CheckCircle2, XCircle } from "lucide-react";

import { RowActionsMenu } from "@/core/components/shared/data-table";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import type { NotificationTemplateDto } from "@/features/notification/api/dto";
import { useToggleNotificationTemplate } from "@/features/notification/api/use-toggle-notification-template";

/**
 * Menu d'actions de ligne : bascule active/inactive
 * (`POST .../{code}/{channel}/{language}/active?isActive=...`). L'activation
 * et la désactivation sont exposées comme deux entrées distinctes pour
 * lever toute ambiguïté (le booléen en query est non-nullable).
 */
export function NotificationTemplateRowActions({
  template,
}: {
  template: NotificationTemplateDto;
}) {
  const toggle = useToggleNotificationTemplate();
  const [preview, setPreview] = useState<"activate" | "deactivate" | null>(
    null
  );
  const nextActive = preview === "activate";

  return (
    <>
      <RowActionsMenu
        actions={[
          template.isActive
            ? {
                id: "deactivate",
                label: t("notifications.templates.actions.deactivate"),
                icon: XCircle,
                variant: "destructive" as const,
                onClick: () => setPreview("deactivate"),
              }
            : {
                id: "activate",
                label: t("notifications.templates.actions.activate"),
                icon: CheckCircle2,
                onClick: () => setPreview("activate"),
              },
        ]}
      />
      <MutationConfirmPreview
        open={preview !== null}
        onOpenChange={(open) => {
          if (!open) setPreview(null);
        }}
        data={{
          code: template.code,
          channel: template.channel,
          language: template.language,
          isActive: template.isActive,
          nextIsActive: nextActive,
        }}
        title={t("common.mutationPreview.statusTitle")}
        description={t("common.mutationPreview.statusDescription")}
        confirmLabel={
          preview === "deactivate"
            ? t("notifications.templates.actions.deactivate")
            : t("notifications.templates.actions.activate")
        }
        t={t}
        pending={toggle.isPending}
        onConfirm={() => {
          if (!preview) return;
          toggle.mutate(
            {
              code: template.code,
              channel: template.channel,
              language: template.language,
              isActive: nextActive,
            },
            {
              onSuccess: () => {
                notifSilence(
                  t(
                    nextActive
                      ? "notifications.templates.feedback.activated"
                      : "notifications.templates.feedback.deactivated"
                  )
                );
                setPreview(null);
              },
              onError: (error) => notifSilenceError(error.message),
            }
          );
        }}
      />
    </>
  );
}
